// CANONICAL viewer inset hook.
//
// This file is the single source of truth. It is copied verbatim into each
// panel by `Scripts/sync_report_renderers.py`; do not edit the panel copies.
//
// The report viewer is portalled to `document.body` so no ancestor with
// `overflow: hidden` can clip it. A portalled fixed overlay would otherwise
// cover the whole window including the sidebar, which hides the app navigation
// while a report is open.
//
// The four panels each build their sidebar differently — Admin uses a
// percentage-width `aside`, Teacher and Student use a collapsible 16rem/5rem
// margin, Parent has its own variant — so the offset is measured from the DOM
// rather than hard-coded. An anchor rendered in place reports where the content
// column actually starts, and a ResizeObserver keeps it correct when the
// sidebar is collapsed or the window is resized.
import { useEffect, useMemo, useRef, useState } from "react";

// Below this width the panels overlay their sidebar anyway, so the report
// should use the full window.
const DESKTOP_BREAKPOINT = 1024;

export function useViewerInset() {
  const anchorRef = useRef(null);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    const container = anchorRef.current?.parentElement;
    if (!container) return undefined;

    const measure = () => {
      if (window.innerWidth < DESKTOP_BREAKPOINT) {
        setLeft(0);
        return;
      }
      const { left: edge } = container.getBoundingClientRect();
      // Guard against a detached or zero-width container.
      setLeft(Number.isFinite(edge) && edge > 0 ? Math.round(edge) : 0);
    };

    measure();
    window.addEventListener("resize", measure);

    // Catches sidebar collapse, which changes the content column's width.
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(container);
      observer.observe(document.body);
    }

    return () => {
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, []);

  const style = useMemo(() => ({ left: `${left}px` }), [left]);
  return { anchorRef, style };
}
